import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import type { Service } from '../../types/api.types';
import { getAdminServices, saveService, updateService, deleteService } from '../../services/storage';

interface ServiceFormData {
  title_de: string;
  title_en: string;
  title_ar: string;
  description_de: string;
  description_en: string;
  description_ar: string;
  icon: string;
  price: string;
}

const EMPTY_FORM: ServiceFormData = {
  title_de: '', title_en: '', title_ar: '',
  description_de: '', description_en: '', description_ar: '',
  icon: '', price: '',
};

const AdminServices: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [deletingService, setDeletingService] = useState<Service | null>(null);
  const [form, setForm] = useState<ServiceFormData>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loadServices = () => { getAdminServices().then(setServices).catch(() => {}); };

  useEffect(() => { loadServices(); }, []);

  const openCreate = () => {
    setEditingService(null);
    setForm(EMPTY_FORM);
    setError(null);
    setDialogOpen(true);
  };

  const openEdit = (service: Service) => {
    setEditingService(service);
    setForm({
      title_de: service.title_de,
      title_en: service.title_en,
      title_ar: service.title_ar,
      description_de: service.description_de,
      description_en: service.description_en,
      description_ar: service.description_ar,
      icon: service.icon ?? '',
      price: service.price ?? '',
    });
    setError(null);
    setDialogOpen(true);
  };

  const openDelete = (service: Service) => {
    setDeletingService(service);
    setDeleteDialogOpen(true);
  };

  const handleChange = (field: keyof ServiceFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.title_de || !form.title_en || !form.title_ar ||
        !form.description_de || !form.description_en || !form.description_ar) {
      setError('Bitte alle Pflichtfelder ausfüllen.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        icon: form.icon || undefined,
        price: form.price || undefined,
        features: [] as string[],
      };
      if (editingService) {
        await updateService(editingService.id, payload);
        setSuccess('Service aktualisiert.');
      } else {
        await saveService(payload);
        setSuccess('Service erstellt.');
      }
      setDialogOpen(false);
      loadServices();
    } catch {
      setError('Fehler beim Speichern. Bitte versuche es erneut.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingService) return;
    await deleteService(deletingService.id);
    setSuccess('Service gelöscht.');
    setDeleteDialogOpen(false);
    loadServices();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Services verwalten</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Service hinzufügen
        </Button>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {error && !dialogOpen && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ border: '1px solid #e8e8e8', boxShadow: 'none' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f7f7f7' }}>
              <TableCell sx={{ fontWeight: 600 }}>Icon</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Titel (DE)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Preis</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Titel (EN)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Titel (AR)</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {services.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  Noch keine Services vorhanden.
                </TableCell>
              </TableRow>
            ) : (
              services.map((service) => (
                <TableRow key={service.id} hover>
                  <TableCell><Typography variant="h5">{service.icon ?? '—'}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{service.title_de}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ color: service.price ? '#1737bc' : 'text.disabled', fontWeight: service.price ? 600 : 400 }}
                    >
                      {service.price ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{service.title_en}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={service.title_ar} size="small" sx={{ fontFamily: 'inherit', direction: 'rtl' }} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => openEdit(service)} size="small" sx={{ mr: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => openDelete(service)} size="small" sx={{ color: 'error.main' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {editingService ? 'Service bearbeiten' : 'Neuen Service erstellen'}
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>Allgemein</Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 3 }}>
            <TextField
              label="Icon (Emoji)"
              value={form.icon}
              onChange={handleChange('icon')}
              sx={{ width: 140 }}
              placeholder="z.B. 📒"
            />
            <TextField
              label="Preis"
              value={form.price}
              onChange={handleChange('price')}
              sx={{ flex: 1 }}
              placeholder="z.B. ab 89 € / Monat"
              helperText='Freitext oder "auf Anfrage"'
            />
          </Box>

          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>Titel</Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 3 }}>
            <TextField label="Titel (Deutsch) *" value={form.title_de} onChange={handleChange('title_de')} fullWidth />
            <TextField label="Title (English) *" value={form.title_en} onChange={handleChange('title_en')} fullWidth />
            <TextField label="العنوان (عربي) *" value={form.title_ar} onChange={handleChange('title_ar')} fullWidth inputProps={{ dir: 'rtl' }} />
          </Box>

          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>Beschreibung</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Beschreibung (Deutsch) *" value={form.description_de} onChange={handleChange('description_de')} fullWidth multiline rows={3} />
            <TextField label="Description (English) *" value={form.description_en} onChange={handleChange('description_en')} fullWidth multiline rows={3} />
            <TextField label="الوصف (عربي) *" value={form.description_ar} onChange={handleChange('description_ar')} fullWidth multiline rows={3} inputProps={{ dir: 'rtl' }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Speichert…' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Service löschen?</DialogTitle>
        <DialogContent>
          <Typography>
            Möchtest du <strong>{deletingService?.title_de}</strong> wirklich löschen?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Löschen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminServices;
